/**
 * Premium Dashboard — first thing users see after login.
 * Quick stats, recent projects, AI credits, activity, pinned tools.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Sparkles, FolderKanban, GraduationCap, Youtube, Pen, FileText,
  TrendingUp, Clock, Zap, BarChart3, ArrowUpRight, Plus, Activity,
  IndianRupee, Trophy, Target, Search as SearchIcon, Video,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { PremiumCard, PremiumButton, SectionHeading, Pill } from "@/components/premium/Primitives";

const API = process.env.REACT_APP_BACKEND_URL || '';

const fadeUp = (d = 0) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, delay: d },
});

const STATS_DEF = [
  { key: "ai_generations_24h", label: "AI today", Icon: Sparkles },
  { key: "ai_generations_7d", label: "AI this week", Icon: TrendingUp },
  { key: "projects", label: "Projects", Icon: FolderKanban },
  { key: "reports", label: "Reports", Icon: BarChart3 },
  { key: "tasks_due", label: "Tasks due", Icon: Clock },
];

const QUICK_ACTIONS = [
  { label: "Generate PPT", Icon: GraduationCap, to: "/tools/ai-slides" },
  { label: "Audit YouTube channel", Icon: Youtube, to: "/youtube-suite/channel-assessment" },
  { label: "Write Instagram post", Icon: Pen, to: "/tools/social-media" },
  { label: "Find keywords", Icon: TrendingUp, to: "/tools/seo/ai" },
];

const PINNED = [
  { label: "AI SEO Toolkit", Icon: Sparkles, to: "/tools/seo/ai", desc: "Live SerpAPI keyword research" },
  { label: "SEO Dashboard", Icon: Target, to: "/tools/seo/dashboard", desc: "Site audit + backlinks + rank" },
  { label: "Creator Incentive", Icon: Trophy, to: "/creator/incentive", desc: "Earn ₹150/video · ₹50/short" },
  { label: "Channel Assessment", Icon: Youtube, to: "/youtube-suite/channel-assessment", desc: "Audit any YT channel in seconds" },
  { label: "AI Thumbnail Studio", Icon: Video, to: "/tools/design/thumbnail-studio", desc: "AI text + reference thumbnails" },
];

export default function PremiumDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [incentive, setIncentive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/premium/dashboard/stats`, { withCredentials: true });
        if (!cancelled) {
          setStats(r.data?.stats || {});
          setActivity(r.data?.recent_activity || []);
        }
      } catch {
        if (!cancelled) { setStats({}); setActivity([]); }
      } finally { if (!cancelled) setLoading(false); }
      try {
        const r2 = await axios.get(`${API}/api/creator-incentive/my-stats`, { withCredentials: true });
        if (!cancelled) setIncentive(r2.data);
      } catch {
        /* not signed up yet — silent */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fmt = (n) => (n === undefined || n === null ? "—" : Number(n).toLocaleString());

  return (
    <div className="min-h-screen bg-slate-50/40 font-sans" data-testid="premium-dashboard">
      <PremiumNav/>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {/* Header */}
        <motion.div {...fadeUp()} className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <SectionHeading
            eyebrow="Dashboard"
            title="Welcome back"
            subtitle="Pick up where you left off — your workspace is ready."
          />
          <PremiumButton icon={Plus} onClick={() => navigate("/workspace")}>
            New project
          </PremiumButton>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10" data-testid="dashboard-stats">
          {STATS_DEF.map((s, i) => (
            <motion.div key={s.key} {...fadeUp(0.04 * i)}>
              <PremiumCard className="p-5" hover={false}>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{s.label}</div>
                  <div className="w-8 h-8 rounded-lg bg-brand-50 text-[#A855F7] flex items-center justify-center">
                    <s.Icon className="w-4 h-4"/>
                  </div>
                </div>
                <div className="mt-3 font-heading text-3xl font-bold text-[#111111]" data-testid={`stat-${s.key}`}>
                  {loading ? "…" : fmt(stats?.[s.key])}
                </div>
                <div className="mt-1 text-xs text-slate-400">
                  {s.key === "ai_generations_24h" && stats?.delta_pct != null
                    ? `${stats.delta_pct >= 0 ? "+" : ""}${stats.delta_pct}% vs last 7d`
                    : (loading ? "" : "Across all suites")}
                </div>
              </PremiumCard>
            </motion.div>
          ))}
        </div>

        {/* Creator Earnings + SEO highlight — high-priority hero row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
          <motion.button
            {...fadeUp(0.24)}
            onClick={() => navigate("/creator/incentive")}
            data-testid="dashboard-creator-earnings"
            className="text-left rounded-2xl p-6 bg-gradient-to-br from-[#A855F7] to-[#7C3AED] text-white shadow-lg hover:shadow-xl transition-shadow"
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-90">
              <Trophy className="w-3.5 h-3.5" /> Creator earnings
            </div>
            <div className="flex items-baseline mt-3">
              <IndianRupee className="w-5 h-5 opacity-90" />
              <span className="text-4xl font-bold">
                {(incentive?.totals?.earnings || 0).toLocaleString("en-IN")}
              </span>
            </div>
            <div className="mt-2 text-sm opacity-90">
              {incentive?.totals?.videos ?? 0} videos · {incentive?.totals?.shorts ?? 0} shorts
              {incentive?.this_month?.amount > 0 && (
                <span className="ml-1">· ₹{incentive.this_month.amount} this month</span>
              )}
            </div>
            <div className="mt-4 text-xs opacity-90 flex items-center gap-1">
              Submit a new video <ArrowUpRight className="w-3.5 h-3.5" />
            </div>
          </motion.button>

          <motion.button
            {...fadeUp(0.28)}
            onClick={() => navigate("/tools/seo")}
            data-testid="dashboard-seo-highlight"
            className="text-left rounded-2xl p-6 bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg hover:shadow-xl transition-shadow"
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-90">
              <Target className="w-3.5 h-3.5" /> SEO toolkit
            </div>
            <div className="font-heading text-2xl font-bold mt-3">
              Rank higher, faster
            </div>
            <div className="mt-2 text-sm opacity-90">
              Live SerpAPI keyword research · Site audit · Backlink analyzer · Rank tracker
            </div>
            <div className="mt-4 text-xs opacity-90 flex items-center gap-1">
              Open SEO tools <ArrowUpRight className="w-3.5 h-3.5" />
            </div>
          </motion.button>

          <motion.button
            {...fadeUp(0.32)}
            onClick={() => navigate("/free/keyword-research")}
            data-testid="dashboard-keyword-highlight"
            className="text-left rounded-2xl p-6 bg-white border border-neutral-200 hover:border-[#A855F7] hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500">
              <SearchIcon className="w-3.5 h-3.5" /> Free keyword tool
            </div>
            <div className="font-heading text-2xl font-bold mt-3 text-[#111111]">
              Hundreds of keywords in 1 click
            </div>
            <div className="mt-2 text-sm text-neutral-600">
              Real search volume · CPC · trend arrows · zero login required
            </div>
            <div className="mt-4 text-xs text-[#A855F7] font-semibold flex items-center gap-1">
              Try free <ArrowUpRight className="w-3.5 h-3.5" />
            </div>
          </motion.button>
        </div>

        {/* Two-col */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick actions */}
          <motion.div {...fadeUp(0.16)} className="lg:col-span-2">
            <PremiumCard className="p-6" hover={false}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading text-lg font-semibold text-[#111111]">Quick actions</h3>
                <Pill variant="brand" icon={Zap}>Most used</Pill>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {QUICK_ACTIONS.map((a) => (
                  <button
                    key={a.label}
                    onClick={() => navigate(a.to)}
                    data-testid={`dashboard-action-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-[#A855F7] hover:bg-brand-50/40 transition-all text-left group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#111111] text-white group-hover:bg-[#A855F7] flex items-center justify-center transition-colors">
                        <a.Icon className="w-4 h-4"/>
                      </div>
                      <span className="text-sm font-semibold text-[#111111]">{a.label}</span>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-[#A855F7] group-hover:translate-x-0.5 transition-all"/>
                  </button>
                ))}
              </div>
            </PremiumCard>

            {/* Recent activity */}
            <motion.div {...fadeUp(0.2)} className="mt-6">
              <PremiumCard className="p-6" hover={false}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-semibold text-[#111111]">Recent activity</h3>
                  <Pill variant="outline" icon={Activity}>Live</Pill>
                </div>
                <div className="space-y-3" data-testid="dashboard-activity">
                  {activity.length === 0 && !loading && (
                    <div className="text-sm text-slate-500 py-3">
                      Your workspace is ready — run any AI tool to see activity here.
                    </div>
                  )}
                  {activity.map((a, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                      <div className="w-8 h-8 rounded-full bg-brand-50 text-[#A855F7] flex items-center justify-center text-xs font-bold">
                        {a.type === "channel-assess" ? <Youtube className="w-3.5 h-3.5"/> : <Sparkles className="w-3.5 h-3.5"/>}
                      </div>
                      <div className="flex-1 text-sm text-slate-700 truncate">
                        <span className="font-semibold text-[#111111]">{a.tool || a.type}</span>
                        {a.label && <span className="text-slate-500"> · {a.label}</span>}
                      </div>
                      <span className="text-xs text-slate-400 whitespace-nowrap">
                        {new Date(a.ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  ))}
                </div>
              </PremiumCard>
            </motion.div>
          </motion.div>

          {/* AI credits + Pinned */}
          <motion.div {...fadeUp(0.18)} className="space-y-6">
            <PremiumCard className="p-6 bg-[#111111] text-white border-[#111111]" hover={false}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-white/60 font-semibold">AI Credits</div>
                  <div className="font-heading text-3xl font-bold mt-1">Unlimited</div>
                </div>
                <Sparkles className="w-6 h-6 text-[#A855F7]"/>
              </div>
              <div className="mt-4 text-sm text-white/70">Super-admin tier · all suites unlocked.</div>
              <button onClick={() => navigate("/settings")} className="mt-4 text-xs text-[#A855F7] font-semibold hover:underline">
                Manage usage →
              </button>
            </PremiumCard>

            <PremiumCard className="p-6" hover={false}>
              <h3 className="font-heading text-lg font-semibold text-[#111111] mb-4">Pinned tools</h3>
              <div className="space-y-2">
                {PINNED.map((p) => (
                  <button
                    key={p.label}
                    onClick={() => navigate(p.to)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left"
                  >
                    <p.Icon className="w-4 h-4 text-[#A855F7]"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#111111] truncate">{p.label}</div>
                      <div className="text-xs text-slate-500 truncate">{p.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </PremiumCard>
          </motion.div>
        </div>
      </div>
      <PremiumFooter />
    </div>
  );
}
